import React from 'react';

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Top-level Error Boundary that catches React rendering errors
 * and displays a crash screen instead of a blank page.
 * This helps with debugging production issues.
 */
export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details for debugging
    console.error('🔴 AppErrorBoundary caught error:', error);
    console.error('🔴 Component stack:', errorInfo.componentStack);
    
    this.setState({ errorInfo });
  }

  private handleReload = () => {
    // Clear any cached data that might cause issues
    sessionStorage.removeItem('__lazy_reload_at');
    
    // Force reload without cache
    window.location.reload();
  };

  private handleClearAndReload = () => {
    // Clear all session/local storage
    try {
      sessionStorage.clear();
      localStorage.clear();
    } catch (e) {
      console.warn('Could not clear storage:', e);
    }
    
    // Force reload
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;
      
      return (
        <div style={{
          minHeight: '100vh',
          backgroundColor: '#0f0f0f',
          color: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            textAlign: 'center'
          }}>
            {/* Error Icon */}
            <div style={{
              fontSize: '48px',
              marginBottom: '16px'
            }}>
              ⚠️
            </div>
            
            {/* Title */}
            <h1 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '8px',
              color: '#ef4444'
            }}>
              แอปพลิเคชันเกิดข้อผิดพลาด
            </h1>
            
            <p style={{
              fontSize: '16px',
              color: '#9ca3af',
              marginBottom: '24px'
            }}>
              Application Error - Please reload or contact support
            </p>
            
            {/* Error Details Box */}
            <div style={{
              backgroundColor: '#1f1f1f',
              border: '1px solid #374151',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'left',
              overflow: 'auto',
              maxHeight: '300px'
            }}>
              <div style={{
                fontSize: '12px',
                color: '#9ca3af',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Error Message
              </div>
              <pre style={{
                fontSize: '14px',
                color: '#f87171',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: '0 0 16px 0'
              }}>
                {error?.message || 'Unknown error'}
              </pre>
              
              {error?.stack && (
                <>
                  <div style={{
                    fontSize: '12px',
                    color: '#9ca3af',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Stack Trace
                  </div>
                  <pre style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    margin: '0 0 16px 0',
                    maxHeight: '150px',
                    overflow: 'auto'
                  }}>
                    {error.stack}
                  </pre>
                </>
              )}
              
              {errorInfo?.componentStack && (
                <>
                  <div style={{
                    fontSize: '12px',
                    color: '#9ca3af',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Component Stack
                  </div>
                  <pre style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    margin: '0',
                    maxHeight: '100px',
                    overflow: 'auto'
                  }}>
                    {errorInfo.componentStack}
                  </pre>
                </>
              )}
            </div>
            
            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={this.handleReload}
                style={{
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                🔄 รีโหลดหน้า
              </button>
              
              <button
                onClick={this.handleClearAndReload}
                style={{
                  backgroundColor: '#374151',
                  color: '#ffffff',
                  border: '1px solid #4b5563',
                  borderRadius: '6px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                🧹 ล้าง Cache แล้วรีโหลด
              </button>
            </div>
            
            {/* Additional Help */}
            <p style={{
              fontSize: '12px',
              color: '#6b7280',
              marginTop: '24px'
            }}>
              หากปัญหายังคงอยู่ กรุณาติดต่อฝ่ายสนับสนุน
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
